import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  Mail,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react'
import { AccountLayout } from '@/components/account/AccountLayout'
import { AccountPageHeader } from '@/components/account/AccountPageHeader'
import { useAuth } from '@/lib/auth'
import { useActiveOrg, useUserProfile } from '@/lib/user-profile'
import {
  createOrg,
  deleteOrg,
  removeMember,
  resendInvite,
  revokeInvite,
  sendInvite,
  updateMemberRole,
  updateOrgName,
  useOrgInvites,
  useOrgMembers,
  useUserOrgs,
  type OrgMember,
} from '@/lib/org'
import { Plus } from 'lucide-react'

type Role = 'owner' | 'admin' | 'member'

function displayName(m: OrgMember): string {
  // The org_members_admin RPC already resolves a display name server-side
  // (First Last → display_name → email local-part → short uuid).
  return m.display_name || m.email || m.user_id.slice(0, 8) + '…'
}

function lastLoginLabel(iso: string | null): string {
  if (!iso) return 'never'
  return relTime(iso)
}

/** Exact local date+time for hover tooltips on relative-time labels. */
function fullDateTime(iso: string | null): string | undefined {
  if (!iso) return undefined
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

function relTime(iso: string): string {
  const d = new Date(iso)
  const days = Math.round((Date.now() - d.getTime()) / 86_400_000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function expiresIn(iso: string): string {
  const d = new Date(iso)
  const days = Math.ceil((d.getTime() - Date.now()) / 86_400_000)
  if (days < 0) return 'expired'
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  return `${days}d`
}

export function AccountOrgPage() {
  const { user, loading: authLoading } = useAuth()
  const { loading: profileLoading, refresh: refreshProfile } = useUserProfile()
  const { org, loading: orgLoading, refresh: refreshOrg } = useActiveOrg()
  const orgId = org?.organization_id ?? null
  const callerRole = org?.caller_role ?? null
  const isAdmin = callerRole === 'owner' || callerRole === 'admin'
  const isOwner = callerRole === 'owner'

  const { members, loading: membersLoading, refresh: refreshMembers } = useOrgMembers(orgId)
  const { invites, loading: invitesLoading, refresh: refreshInvites } = useOrgInvites(orgId)
  const { orgs: userOrgs, refresh: refreshUserOrgs } = useUserOrgs()
  const navigate = useNavigate()
  // Owners can delete the org, but the orphan-owner trigger in the DB
  // also blocks the delete if it would leave the owner with zero orgs.
  // Hide the danger zone in that case so the UI doesn't dangle an
  // affordance that the server is going to reject.
  const hasMultipleOrgs = userOrgs.length > 1

  if (!authLoading && !user) return <Navigate to="/sign-in" replace />
  if (profileLoading || orgLoading) {
    return (
      <AccountLayout>
        <p className="text-[var(--color-muted)]">Loading…</p>
      </AccountLayout>
    )
  }
  if (!org || !orgId) {
    return (
      <AccountLayout>
        <p className="text-[14px] text-[var(--color-muted)]">
          No active organization. Open your account dashboard to set one.
        </p>
      </AccountLayout>
    )
  }

  const refreshAll = async () => {
    await Promise.all([
      refreshUserOrgs(),
      refreshProfile(),
      refreshOrg(),
      refreshMembers(),
      refreshInvites(),
    ])
  }

  return (
    <AccountLayout>
      <AccountPageHeader
        title="Organization"
        subtitle="Manage your organization's name, members, and invites."
        action={<NewOrgButton onChanged={refreshAll} />}
        orgId={orgId ?? ''}
        onOrgChange={refreshAll}
      />

      <NameCard
        orgId={orgId}
        orgName={org.name}
        isAdmin={isAdmin}
        onSaved={async () => {
          await refreshOrg()
        }}
      />

      {/* Members + invites + danger are admin-only. Plain members get a
          trimmed read-only view: org name (above) + their own role, no
          member list, no invite/remove. */}
      {isAdmin ? (
        <>
          <MembersSection
            members={members}
            loading={membersLoading}
            callerRole={callerRole}
            currentUserId={user?.id ?? null}
            onChanged={async () => {
              await Promise.all([refreshMembers(), refreshOrg()])
            }}
            orgId={orgId}
          />

          <InvitesSection
            orgId={orgId}
            invites={invites}
            loading={invitesLoading}
            isAdmin={isAdmin}
            onChanged={async () => {
              await Promise.all([refreshInvites(), refreshOrg()])
            }}
          />
        </>
      ) : (
        <MemberRoleNote role={callerRole} />
      )}

      {isAdmin && isOwner && hasMultipleOrgs ? (
        <DangerZone
          orgId={orgId}
          orgName={org.name}
          onDeleted={async () => {
            // After deleting the org, refresh and let useActiveOrg pick
            // up the next active. /account/org will re-render against
            // whichever org becomes active.
            await Promise.all([refreshUserOrgs(), refreshProfile()])
            navigate('/account/org', { replace: true })
          }}
        />
      ) : null}
    </AccountLayout>
  )
}

// -----------------------------------------------------------------------------

interface NewOrgButtonProps {
  onChanged: () => Promise<void> | void
}
/**
 * Compact "+ New organization" affordance that lives on the top action
 * bar next to the OrgSelector. Clicking expands an inline modal-style
 * panel anchored below the button. The previous "Organizations" section
 * (with a "You belong to N" sub-header) was redundant chrome — the
 * dropdown already tells the user which orgs they belong to, so all
 * that was left to surface was the create action itself.
 */
function NewOrgButton({ onChanged }: NewOrgButtonProps) {
  const [open, setOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function onCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setErr(null)
    if (!newName.trim()) return
    setBusy(true)
    const { error } = await createOrg(newName.trim())
    setBusy(false)
    if (error) {
      setErr(error)
      return
    }
    setNewName('')
    setOpen(false)
    await onChanged()
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[var(--radius-DEFAULT)] bg-[var(--color-accent)] text-[var(--color-surface)] text-[12.5px] font-medium hover:bg-[var(--color-accent-hover)] cursor-pointer"
      >
        <Plus size={13} aria-hidden />
        {open ? 'Close' : 'New organization'}
      </button>

      {open ? (
        <form
          onSubmit={onCreate}
          className={[
            'absolute right-0 z-10 mt-2 w-80',
            'rounded-[var(--radius-DEFAULT)] border border-[var(--color-border)]',
            'bg-[var(--color-elev)] p-4 shadow-lg',
            'flex flex-col gap-3',
          ].join(' ')}
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-[11.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
              Organization name
            </span>
            <input
              type="text"
              required
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Acme Cartography"
              className={[
                'px-3 py-2 rounded-[var(--radius-DEFAULT)]',
                'bg-[var(--color-surface)] border border-[var(--color-border)]',
                'text-[14px] text-[var(--color-text)]',
                'focus:outline-none focus:border-[var(--color-accent)]',
              ].join(' ')}
            />
          </label>
          <button
            type="submit"
            disabled={busy || !newName.trim()}
            className="inline-flex items-center justify-center px-4 py-2 rounded-[var(--radius-DEFAULT)] bg-[var(--color-accent)] text-[var(--color-surface)] text-[13px] font-medium hover:bg-[var(--color-accent-hover)] disabled:opacity-60 cursor-pointer"
          >
            {busy ? 'Creating…' : 'Create + switch'}
          </button>
          {err ? <p className="text-[12.5px] text-[var(--color-signal)]">{err}</p> : null}
        </form>
      ) : null}
    </div>
  )
}

// -----------------------------------------------------------------------------

interface NameCardProps {
  orgId: string
  orgName: string
  isAdmin: boolean
  onSaved: () => Promise<void> | void
}
/**
 * Org name display + rename. Previously this was rendered as the page's
 * big H1 title with an inline Rename button beside it, but the page
 * header now says "Organization" (matching the sidebar nav label), so
 * the org name itself moves into its own section card with a clearer
 * label / input affordance.
 */
function NameCard({ orgId, orgName, isAdmin, onSaved }: NameCardProps) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(orgName)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setName(orgName)
  }, [orgName])

  async function onSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setErr(null)
    if (name.trim().length === 0) {
      setErr('Name is required.')
      return
    }
    setSaving(true)
    const { error } = await updateOrgName(orgId, name)
    setSaving(false)
    if (error) {
      setErr(error)
      return
    }
    setEditing(false)
    await onSaved()
  }

  return (
    <section className="mb-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-6">
      <h2 className="text-[14px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
        Name
      </h2>
      {editing ? (
        <form onSubmit={onSave} className="mt-3 flex items-center gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={[
              'flex-1 px-3 py-2 rounded-[var(--radius-DEFAULT)]',
              'bg-[var(--color-surface)] border border-[var(--color-border)]',
              'text-[15px] text-[var(--color-text-strong)]',
              'focus:outline-none focus:border-[var(--color-accent)]',
            ].join(' ')}
            autoFocus
          />
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-3 py-2 rounded-[var(--radius-DEFAULT)] bg-[var(--color-accent)] text-[var(--color-surface)] text-[13px] font-medium hover:bg-[var(--color-accent-hover)] disabled:opacity-60 cursor-pointer"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false)
              setName(orgName)
              setErr(null)
            }}
            className="inline-flex items-center px-3 py-2 rounded-[var(--radius-DEFAULT)] text-[13px] text-[var(--color-muted)] hover:text-[var(--color-text)] cursor-pointer"
          >
            Cancel
          </button>
        </form>
      ) : (
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="flex-1 px-3 py-2 rounded-[var(--radius-DEFAULT)] bg-[var(--color-surface)] border border-[var(--color-border)] text-[15px] text-[var(--color-text-strong)] truncate">
            {orgName}
          </p>
          {isAdmin ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center px-3 py-2 rounded-[var(--radius-DEFAULT)] border border-[var(--color-border)] text-[13px] text-[var(--color-text)] hover:border-[var(--color-accent)]/50 hover:text-[var(--color-accent)] cursor-pointer transition-colors"
            >
              Rename
            </button>
          ) : null}
        </div>
      )}
      {err ? <p className="mt-2 text-[12.5px] text-[var(--color-signal)]">{err}</p> : null}
    </section>
  )
}

// -----------------------------------------------------------------------------

/** Trimmed read-only note shown to plain members in place of the member list. */
function MemberRoleNote({ role }: { role: Role | null }) {
  return (
    <section className="mb-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-6">
      <h2 className="text-[15px] font-semibold tracking-tight text-[var(--color-text-strong)]">
        Members
      </h2>
      <p className="mt-2 text-[13px] text-[var(--color-muted)]">
        You're a <span className="capitalize text-[var(--color-text)]">{role ?? 'member'}</span> of this
        organization. Member management is available to owners and admins.
      </p>
    </section>
  )
}

type MemberSortCol = 'name' | 'email' | 'joined' | 'last_login' | 'role'
type SortDir = 'asc' | 'desc'

interface MembersSectionProps {
  orgId: string
  members: OrgMember[]
  loading: boolean
  callerRole: Role | null
  currentUserId: string | null
  onChanged: () => Promise<void> | void
}
function MembersSection({ orgId, members, loading, callerRole, currentUserId, onChanged }: MembersSectionProps) {
  const isAdmin = callerRole === 'owner' || callerRole === 'admin'
  const [busyId, setBusyId] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sortCol, setSortCol] = useState<MemberSortCol>('joined')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [removeTarget, setRemoveTarget] = useState<OrgMember | null>(null)

  async function handleRoleChange(member: OrgMember, role: Role) {
    setBusyId(member.user_id)
    setErr(null)
    const { error } = await updateMemberRole(orgId, member.user_id, role)
    setBusyId(null)
    if (error) {
      setErr(error)
      return
    }
    await onChanged()
  }

  async function confirmRemove() {
    if (!removeTarget) return
    const member = removeTarget
    setBusyId(member.user_id)
    setErr(null)
    const { error } = await removeMember(orgId, member.user_id)
    setBusyId(null)
    setRemoveTarget(null)
    if (error) {
      setErr(error)
      return
    }
    await onChanged()
  }

  function toggleSort(col: MemberSortCol) {
    if (col === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortCol(col)
      setSortDir('asc')
    }
  }

  const q = search.trim().toLowerCase()
  const visible = members
    .filter((m) =>
      q === '' ||
      displayName(m).toLowerCase().includes(q) ||
      (m.email ?? '').toLowerCase().includes(q),
    )
    .slice()
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1
      const roleRank = (r: Role) => (r === 'owner' ? 0 : r === 'admin' ? 1 : 2)
      let cmp = 0
      switch (sortCol) {
        case 'name': cmp = displayName(a).localeCompare(displayName(b)); break
        case 'email': cmp = (a.email ?? '').localeCompare(b.email ?? ''); break
        case 'joined': cmp = a.joined_at.localeCompare(b.joined_at); break
        case 'role': cmp = roleRank(a.role) - roleRank(b.role); break
        case 'last_login': {
          // Nulls (never logged in) sort last regardless of direction.
          const av = a.last_sign_in_at, bv = b.last_sign_in_at
          if (!av && !bv) cmp = 0
          else if (!av) return 1
          else if (!bv) return -1
          else cmp = av.localeCompare(bv)
          break
        }
      }
      return cmp * dir
    })

  return (
    <section className="mb-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-[15px] font-semibold tracking-tight text-[var(--color-text-strong)]">
          Members
        </h2>
        <span className="text-[12px] text-[var(--color-muted)]">
          {members.length} {members.length === 1 ? 'member' : 'members'}
        </span>
      </div>
      {err ? <p className="mt-3 text-[12.5px] text-[var(--color-signal)]">{err}</p> : null}

      {members.length > 0 ? (
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email…"
          className={[
            'mt-3 w-full sm:w-72 px-3 py-1.5 rounded-[var(--radius-DEFAULT)]',
            'bg-[var(--color-surface)] border border-[var(--color-border)]',
            'text-[13px] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)]',
          ].join(' ')}
        />
      ) : null}

      {loading ? (
        <p className="mt-3 text-[13px] text-[var(--color-muted)]">Loading…</p>
      ) : members.length === 0 ? (
        <p className="mt-3 text-[13px] text-[var(--color-muted)]">No members.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]">
                <MemberTh label="Name" col="name" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                <MemberTh label="Email" col="email" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                <MemberTh label="Joined" col="joined" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                <MemberTh label="Last login" col="last_login" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                <MemberTh label="Role" col="role" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                {isAdmin ? <th className="py-2 pl-3 font-medium" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {visible.map((m) => {
                const isSelf = m.user_id === currentUserId
                const canManage = isAdmin && !isSelf
                return (
                  <tr key={m.user_id}>
                    <td className="py-2.5 pr-3 font-medium text-[var(--color-text-strong)]">
                      {displayName(m)}{' '}
                      {isSelf ? <span className="text-[11px] font-normal text-[var(--color-muted)]">(you)</span> : null}
                    </td>
                    <td className="py-2.5 pr-3 text-[var(--color-muted)]">{m.email ?? '—'}</td>
                    <td className="py-2.5 pr-3 text-[var(--color-muted)] whitespace-nowrap" title={fullDateTime(m.joined_at)}>{relTime(m.joined_at)}</td>
                    <td className="py-2.5 pr-3 text-[var(--color-muted)] whitespace-nowrap" title={fullDateTime(m.last_sign_in_at)}>{lastLoginLabel(m.last_sign_in_at)}</td>
                    <td className="py-2.5 pr-3">
                      {canManage ? (
                        <select
                          value={m.role}
                          disabled={busyId === m.user_id}
                          onChange={(e) => handleRoleChange(m, e.target.value as Role)}
                          className="px-2 py-1 rounded-[var(--radius-DEFAULT)] bg-[var(--color-surface)] border border-[var(--color-border)] text-[12.5px] cursor-pointer"
                        >
                          <option value="owner">Owner</option>
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                        </select>
                      ) : (
                        <span className="capitalize text-[var(--color-muted)]">{m.role}</span>
                      )}
                    </td>
                    {isAdmin ? (
                      <td className="py-2.5 pl-3 text-right">
                        {canManage ? (
                          <button
                            type="button"
                            onClick={() => setRemoveTarget(m)}
                            disabled={busyId === m.user_id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[var(--radius-DEFAULT)] text-[12.5px] text-[var(--color-muted)] hover:text-[var(--color-signal)] hover:bg-[var(--color-signal-soft)] cursor-pointer disabled:opacity-50 whitespace-nowrap"
                          >
                            <Trash2 size={13} aria-hidden />
                            Remove
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {removeTarget ? (
        <ConfirmModal
          title={`Remove ${displayName(removeTarget)}?`}
          message={<>They will lose access to <span className="text-[var(--color-text)]">this organization</span> and its API keys + usage. You can re-invite them later.</>}
          confirmLabel="Remove member"
          busy={busyId === removeTarget.user_id}
          onConfirm={confirmRemove}
          onClose={() => setRemoveTarget(null)}
        />
      ) : null}
    </section>
  )
}

interface MemberThProps {
  label: string
  col: MemberSortCol
  sortCol: MemberSortCol
  sortDir: SortDir
  onSort: (c: MemberSortCol) => void
}
function MemberTh({ label, col, sortCol, sortDir, onSort }: MemberThProps) {
  const active = col === sortCol
  return (
    <th className="py-2 pr-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 hover:text-[var(--color-text)] cursor-pointer uppercase"
      >
        {label}
        <span className={active ? 'text-[var(--color-accent)]' : 'opacity-30'}>
          {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  )
}

// -----------------------------------------------------------------------------

interface InvitesSectionProps {
  orgId: string
  invites: { id: string; email: string; role: 'admin' | 'member'; expires_at: string; token: string; created_at: string }[]
  loading: boolean
  isAdmin: boolean
  onChanged: () => Promise<void> | void
}
function InvitesSection({ orgId, invites, loading, isAdmin, onChanged }: InvitesSectionProps) {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'member'>('member')
  const [sending, setSending] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string } | null>(null)

  async function onSend(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setErr(null)
    setOk(null)
    setSending(true)
    const { error } = await sendInvite(orgId, email.trim(), role)
    setSending(false)
    if (error) {
      setErr(error)
      return
    }
    setOk(`Invite sent to ${email.trim()}.`)
    setEmail('')
    await onChanged()
  }

  async function confirmRevoke() {
    if (!revokeTarget) return
    const target = revokeTarget
    setBusyId(target.id)
    setErr(null); setOk(null)
    const { error } = await revokeInvite(target.id)
    setBusyId(null)
    setRevokeTarget(null)
    if (error) { setErr(error); return }
    await onChanged()
  }

  async function onResend(inv: { id: string; email: string; role: 'admin' | 'member' }) {
    setBusyId(inv.id)
    setErr(null); setOk(null)
    const { error } = await resendInvite(orgId, inv)
    setBusyId(null)
    if (error) { setErr(error); return }
    setOk(`Invite resent to ${inv.email}.`)
    await onChanged()
  }

  return (
    <section className="mb-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-6">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[15px] font-semibold tracking-tight text-[var(--color-text-strong)]">
          Pending invites
        </h2>
        {isAdmin ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[var(--radius-DEFAULT)] bg-[var(--color-accent)] text-[var(--color-surface)] text-[12.5px] font-medium hover:bg-[var(--color-accent-hover)] cursor-pointer"
          >
            <Mail size={13} aria-hidden />
            {open ? 'Close' : 'Invite member'}
          </button>
        ) : null}
      </div>

      {open ? (
        <form onSubmit={onSend} className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
          <label className="flex-1 flex flex-col gap-1.5">
            <span className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
              Email
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="teammate@example.com"
              className={[
                'px-3 py-2 rounded-[var(--radius-DEFAULT)]',
                'bg-[var(--color-surface)] border border-[var(--color-border)]',
                'text-[14px] text-[var(--color-text)]',
                'focus:outline-none focus:border-[var(--color-accent)]',
              ].join(' ')}
            />
          </label>
          <label className="flex flex-col gap-1.5 sm:w-36">
            <span className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
              Role
            </span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'member')}
              className={[
                'px-3 py-2 rounded-[var(--radius-DEFAULT)]',
                'bg-[var(--color-surface)] border border-[var(--color-border)]',
                'text-[14px] text-[var(--color-text)] cursor-pointer',
              ].join(' ')}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={sending}
            className="inline-flex items-center justify-center px-4 py-2 rounded-[var(--radius-DEFAULT)] bg-[var(--color-accent)] text-[var(--color-surface)] text-[13px] font-medium hover:bg-[var(--color-accent-hover)] disabled:opacity-60 cursor-pointer"
          >
            {sending ? 'Sending…' : 'Send invite'}
          </button>
        </form>
      ) : null}

      {err ? <p className="mt-3 text-[12.5px] text-[var(--color-signal)]">{err}</p> : null}
      {ok ? <p className="mt-3 text-[12.5px] text-[var(--color-accent)]">{ok}</p> : null}

      {loading ? (
        <p className="mt-3 text-[13px] text-[var(--color-muted)]">Loading…</p>
      ) : invites.length === 0 ? (
        <p className="mt-3 text-[13px] text-[var(--color-muted)]">No pending invites.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]">
                <th className="py-2 pr-3 font-medium">Email</th>
                <th className="py-2 pr-3 font-medium">Role</th>
                <th className="py-2 pr-3 font-medium">Sent</th>
                <th className="py-2 pr-3 font-medium">Expires</th>
                {isAdmin ? <th className="py-2 pl-3 text-right font-medium">Actions</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {invites.map((inv) => (
                <tr key={inv.id}>
                  <td className="py-2.5 pr-3 font-medium text-[var(--color-text-strong)]">{inv.email}</td>
                  <td className="py-2.5 pr-3 capitalize text-[var(--color-muted)]">{inv.role}</td>
                  <td className="py-2.5 pr-3 text-[var(--color-muted)] whitespace-nowrap" title={fullDateTime(inv.created_at)}>{relTime(inv.created_at)}</td>
                  <td className="py-2.5 pr-3 text-[var(--color-muted)] whitespace-nowrap" title={fullDateTime(inv.expires_at)}>{expiresIn(inv.expires_at)}</td>
                  {isAdmin ? (
                    <td className="py-2.5 pl-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => onResend(inv)}
                          disabled={busyId === inv.id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[var(--radius-DEFAULT)] text-[12.5px] text-[var(--color-muted)] hover:text-[var(--color-accent)] hover:bg-[var(--color-accent-soft)] cursor-pointer disabled:opacity-50 whitespace-nowrap"
                        >
                          <RefreshCw size={13} aria-hidden />
                          Resend
                        </button>
                        <button
                          type="button"
                          onClick={() => setRevokeTarget({ id: inv.id, email: inv.email })}
                          disabled={busyId === inv.id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[var(--radius-DEFAULT)] text-[12.5px] text-[var(--color-muted)] hover:text-[var(--color-signal)] hover:bg-[var(--color-signal-soft)] cursor-pointer disabled:opacity-50 whitespace-nowrap"
                        >
                          <X size={13} aria-hidden />
                          Revoke
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revokeTarget ? (
        <ConfirmModal
          title={`Revoke invite to ${revokeTarget.email}?`}
          message="The invitation link will stop working immediately. You can send a new invite later."
          confirmLabel="Revoke invite"
          busy={busyId === revokeTarget.id}
          onConfirm={confirmRevoke}
          onClose={() => setRevokeTarget(null)}
        />
      ) : null}
    </section>
  )
}

// -----------------------------------------------------------------------------

// Reusable confirm modal (replaces window.confirm for destructive actions).
interface ConfirmModalProps {
  title: string
  message: ReactNode
  confirmLabel: string
  busy?: boolean
  onConfirm: () => void
  onClose: () => void
}
function ConfirmModal({ title, message, confirmLabel, busy, onConfirm, onClose }: ConfirmModalProps) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="w-full max-w-md rounded-[var(--radius-lg)] p-6 border border-[var(--color-signal)]/50 bg-[var(--color-elev)] shadow-[var(--shadow-results)]">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-[var(--color-signal-soft)] text-[var(--color-signal)] shrink-0">
            <Trash2 size={18} aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold tracking-tight text-[var(--color-text-strong)] m-0">{title}</h2>
            <p className="mt-1 text-[13px] text-[var(--color-muted)]">{message}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="px-4 py-2 rounded-[var(--radius-DEFAULT)] border border-[var(--color-border)] text-[13px] text-[var(--color-text)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]/50 transition-colors cursor-pointer disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[var(--radius-DEFAULT)] bg-[var(--color-signal)] text-white text-[13px] font-medium hover:bg-[var(--color-signal-hover)] transition-colors cursor-pointer disabled:opacity-60">
            <Trash2 size={13} aria-hidden />
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

interface DangerZoneProps {
  orgId: string
  orgName: string
  onDeleted: () => void
}
function DangerZone({ orgId, orgName, onDeleted }: DangerZoneProps) {
  // The actual delete happens behind a modal that requires the user to
  // type BOTH "DELETE" and the org name. Two distinct confirmations to
  // prevent muscle-memory mistakes (e.g. switching orgs mid-task and
  // wiping the wrong one). The button on the danger card just opens
  // the modal — typing happens there.
  const [open, setOpen] = useState(false)

  return (
    <section className="mt-10 rounded-[var(--radius-lg)] border border-[var(--color-signal)]/30 bg-[var(--color-elev)] p-6">
      <h2 className="text-[15px] font-semibold tracking-tight text-[var(--color-signal)]">
        Danger zone
      </h2>
      <p className="mt-1 text-[12.5px] text-[var(--color-muted)]">
        Deleting an organization removes every API key, every member, and every
        usage record. There is no undo.
      </p>
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-[var(--radius-DEFAULT)] bg-[var(--color-signal)] text-white text-[13px] font-medium hover:bg-[var(--color-signal-hover)] cursor-pointer"
        >
          <Trash2 size={13} aria-hidden />
          Delete organization
        </button>
      </div>

      {open ? (
        <DeleteOrgModal
          orgId={orgId}
          orgName={orgName}
          onClose={() => setOpen(false)}
          onDeleted={() => {
            setOpen(false)
            onDeleted()
          }}
        />
      ) : null}
    </section>
  )
}

interface DeleteOrgModalProps {
  orgId: string
  orgName: string
  onClose: () => void
  onDeleted: () => void
}
function DeleteOrgModal({ orgId, orgName, onClose, onDeleted }: DeleteOrgModalProps) {
  const [literal, setLiteral] = useState('')
  const [nameInput, setNameInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const literalOk = literal.trim() === 'DELETE'
  const nameOk = nameInput.trim() === orgName
  const canDelete = literalOk && nameOk && !busy

  async function onConfirm() {
    setErr(null)
    if (!canDelete) return
    setBusy(true)
    const { error } = await deleteOrg(orgId)
    setBusy(false)
    if (error) {
      setErr(error)
      return
    }
    onDeleted()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-org-heading"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
    >
      <div
        className={[
          'w-full max-w-md rounded-[var(--radius-lg)] p-6',
          'border border-[var(--color-signal)]/50',
          'bg-[var(--color-elev)] shadow-[var(--shadow-results)]',
        ].join(' ')}
      >
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-[var(--color-signal-soft)] text-[var(--color-signal)] shrink-0">
            <Trash2 size={18} aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 id="delete-org-heading" className="text-[17px] font-semibold tracking-tight text-[var(--color-text-strong)] m-0">
              Delete <span className="font-[var(--font-mono)] text-[15px]">{orgName}</span>?
            </h2>
            <p className="mt-1 text-[13px] text-[var(--color-muted)]">
              Every API key, member, invite, and usage log will be removed.
              This cannot be undone.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-muted)]">
              Type <span className="font-[var(--font-mono)] text-[var(--color-text)]">DELETE</span> to confirm
            </span>
            <input
              type="text"
              autoFocus
              value={literal}
              onChange={(e) => setLiteral(e.target.value)}
              placeholder="DELETE"
              className="px-3 py-2 rounded-[var(--radius-DEFAULT)] bg-[var(--color-surface)] border border-[var(--color-border)] text-[14px] focus:outline-none focus:border-[var(--color-signal)]"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-muted)]">
              Then type the org name: <span className="font-[var(--font-mono)] text-[var(--color-text)]">{orgName}</span>
            </span>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder={orgName}
              className="px-3 py-2 rounded-[var(--radius-DEFAULT)] bg-[var(--color-surface)] border border-[var(--color-border)] text-[14px] focus:outline-none focus:border-[var(--color-signal)]"
            />
          </label>
        </div>

        {err ? (
          <p role="alert" className="mt-3 text-[12.5px] text-[var(--color-signal)]">{err}</p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={[
              'px-4 py-2 rounded-[var(--radius-DEFAULT)]',
              'border border-[var(--color-border)] text-[13px]',
              'text-[var(--color-text)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]/50',
              'transition-colors cursor-pointer disabled:opacity-60',
            ].join(' ')}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canDelete}
            className={[
              'inline-flex items-center gap-1.5 px-4 py-2 rounded-[var(--radius-DEFAULT)]',
              'bg-[var(--color-signal)] text-white text-[13px] font-medium',
              'hover:bg-[var(--color-signal-hover)] transition-colors',
              'cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
            ].join(' ')}
          >
            <Trash2 size={13} aria-hidden />
            {busy ? 'Deleting…' : 'Delete organization'}
          </button>
        </div>
      </div>
    </div>
  )
}
